import { useEffect, useState } from 'react';
import { useNavigate, type NavigateFunction } from 'react-router';
import { CButton, CCard, CCardBody, CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle } from '@coreui/react';
import { driver } from 'driver.js';
import { api, type Schemas } from '../api/client';
import './tour.css';

type Contract = Schemas['ContractResponse'];

/**
 * Celý cvičný průběh — happy path z `.context/user-stories.md`. Číslo kroku vidí člověk
 * v pruhu nahoře, aby věděl, kolik ještě zbývá a kde je konec.
 */
export const practiceSteps = [
  { role: 'obchodník', label: 'založí zakázku po telefonu' },
  { role: 'obchodník', label: 'pošle ji ke kontrole' },
  { role: 'vedení', label: 'doplní konfiguraci' },
  { role: 'vedení', label: 'přiřadí výrobu' },
  { role: 'vedení', label: 'schválí k výrobě' },
  { role: 'výroba', label: 'zahájí výrobu' },
  { role: 'výroba', label: 'zeptá se na nejasnost' },
  { role: 'vedení', label: 'odpoví' },
  { role: 'výroba', label: 'potvrdí dokončení' },
  { role: 'vedení', label: 'akceptuje' },
  { role: 'obchodník', label: 'předá zákazníkovi' },
] as const;

type Coach = { step: number; target: string; text: string } | { done: true };

/**
 * Co je na řadě. Kouč nemá vlastní scénář — <b>čte stav zakázky</b>, takže když člověk
 * odejde a vrátí se, naváže tam, kde zakázka skutečně je. (Krok 1, založení, je na
 * formuláři „Nová zakázka“ — viz {@link startPracticeForm}.)
 */
function decide(
  contract: Contract,
  questions: Schemas['QuestionResponse'][],
  assignments: Schemas['AssignmentResponse'][],
  operations: Schemas['AvailableOperationResponse'][],
): Coach {
  const next = 'next-step-button';
  // Hotova je kazda ulozena konfigurace - parametry jsou volne.
  const incomplete = contract.items.some((i) => !i.configuration);

  // Kdyz hlavni krok nejde, kouc nesmi zmlknout - rekne proc a ukaze, co opravit.
  const primary = operations.find((o) => o.buttonStyle === 'primary');
  // Kontrola (kroky 3-5) a otevreny dotaz (krok 8) blokuji hlavni krok zamerne - to jsou
  // samy kroky cviceni, ne zaseknuti.
  const waitingForAnswer = questions.some((q) => q.status === 'Open');
  if (primary && !primary.enabled && contract.state.code !== 'IN_REVIEW' && !waitingForAnswer) {
    const why = primary.blockingReasons.map((r) => r.message).join(' ');
    return incomplete
      ? { step: stepOf(contract.state.code), target: 'configure-item', text: `„${primary.name}“ zatím nejde: ${why}` }
      : { step: stepOf(contract.state.code), target: 'next-step', text: `„${primary.name}“ zatím nejde: ${why}` };
  }

  switch (contract.state.code) {
    case 'DRAFT':
      return { step: 2, target: next, text: 'Zakázka je založená. Pošlete ji ke kontrole — dál ji převezme vedení.' };
    case 'IN_REVIEW':
      if (incomplete) {
        return {
          step: 3,
          target: 'configure-item',
          text: 'Doplňte konfiguraci: vyberte krabici, zapněte GPS nebo LTE a připište parametry, které dílna potřebuje vědět — třeba svorkovnici. Pak uložte.',
        };
      }
      if (!assignments.some((a) => a.roleCode === 'ASSEMBLER')) {
        return { step: 4, target: 'add-assignment', text: 'Přiřaďte někoho z výroby — vyberte sami sebe, na cvičné zakázce hrajete všechny role.' };
      }
      return { step: 5, target: next, text: 'Konfigurace i výroba jsou přiřazené. Schvalte zakázku k výrobě.' };
    case 'APPROVED_FOR_PRODUCTION':
      return { step: 6, target: next, text: 'Zakázka čeká na vás v dílně. Zahajte výrobu.' };
    case 'IN_ASSEMBLY':
      if (questions.length === 0) {
        return { step: 7, target: 'add-question', text: 'V dílně narazíte na nejasnost. Položte dotaz třeba na vedení — dokud nebude vyřízený, zakázka se nedokončí.' };
      }
      if (questions.some((q) => q.status === 'Open')) {
        return { step: 8, target: 'answer-question', text: 'Výroba se ptá vás. Odpovězte na dotaz — zakázka se tím odblokuje.' };
      }
      return { step: 9, target: next, text: 'Dotaz je vyřízený, kus je postavený. Potvrďte dokončení. (Průvodku do dílny najdete vpravo nahoře.)' };
    case 'ASSEMBLY_DONE':
      return { step: 10, target: next, text: 'Zkontrolujte hotový kus a akceptujte ho.' };
    case 'READY':
      return { step: 11, target: next, text: 'Poslední krok: předejte zakázku zákazníkovi.' };
    default:
      return { done: true };
  }
}

/** Na jakém kroku zakázka v daném stavu je — pro chvíli, kdy hlavní krok nejde provést. */
function stepOf(state: string) {
  return ({ DRAFT: 2, IN_REVIEW: 5, APPROVED_FOR_PRODUCTION: 6, IN_ASSEMBLY: 9, ASSEMBLY_DONE: 10, READY: 11 } as Record<string, number>)[state] ?? 1;
}

const roleOf = (step: number) => practiceSteps[step - 1]?.role ?? '';

/** Pruh nahoře: že je to cvičení, kolikátý krok, jakou roli člověk hraje. */
export function PracticeBanner({ step, onExit }: { step: number; onExit: () => void }) {
  return (
    <CCard className="mb-4 st-practice">
      <CCardBody className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
        <div>
          <strong>Cvičná zakázka · krok {step} z {practiceSteps.length}</strong>
          {' '}— teď hrajete: <strong>{roleOf(step)}</strong>. Vidíte ji jen vy, nic se nikomu neodesílá.
          <div className="st-practice-progress mt-2" aria-hidden>
            {practiceSteps.map((s, i) => <span key={s.label} className={i < step ? 'done' : ''} />)}
          </div>
        </div>
        <CButton color="secondary" variant="ghost" onClick={onExit}>Ukončit cvičení</CButton>
      </CCardBody>
    </CCard>
  );
}

export function PracticeCoach({ contract, version }: { contract: Contract; version: number }) {
  const navigate = useNavigate();
  const [coach, setCoach] = useState<Coach | null>(null);

  useEffect(() => {
    const params = { params: { path: { contractId: contract.id } } };
    Promise.all([
      api.GET('/api/v1/contracts/{contractId}/questions', params),
      api.GET('/api/v1/contracts/{contractId}/assignments', params),
      api.GET('/api/v1/contracts/{contractId}/operations', params),
    ]).then(([q, a, o]) => setCoach(decide(contract, q.data ?? [], a.data ?? [], o.data ?? [])));
  }, [contract, version]);

  // Zvyrazni jeden prvek. Klik na nej zvyrazneni zrusi - otevira se tim casto dialog nebo
  // formular a prekryv by prekazel. Dalsi krok se ukaze sam, az se zakazka zmeni.
  useEffect(() => {
    if (!coach || 'done' in coach) return;

    const tour = driver({ popoverClass: 'st-tour', stagePadding: 6, stageRadius: 6, showButtons: ['close'] });
    let element: Element | null = null;
    const release = () => tour.destroy();
    let attempts = 0;

    // Sekce se nacitaji samy - prvek tam nemusi byt hned.
    const timer = window.setInterval(() => {
      element = document.querySelector(`[data-tour="${coach.target}"]`);
      if (element || ++attempts > 10) {
        window.clearInterval(timer);
        if (!element) return;
        element.addEventListener('click', release, { once: true });
        tour.highlight({
          element,
          popover: { title: `Krok ${coach.step} z ${practiceSteps.length} · jste ${roleOf(coach.step)}`, description: coach.text },
        });
      }
    }, 300);

    return () => {
      window.clearInterval(timer);
      element?.removeEventListener('click', release);
      tour.destroy();
    };
  }, [coach]);

  async function finish() {
    await api.DELETE('/api/v1/contracts/{id}', {
      params: { path: { id: contract.id }, query: { reason: 'Cvičení dokončeno' } },
    });
    navigate('/');
  }

  if (!coach) return null;

  if ('done' in coach) {
    return (
      <CModal visible backdrop="static" onClose={() => void finish()}>
        <CModalHeader closeButton={false}><CModalTitle>Hotovo — tohle je celý život zakázky</CModalTitle></CModalHeader>
        <CModalBody>
          <ol className="mb-3">
            {practiceSteps.map((s) => <li key={s.label}><strong>{s.role}</strong> {s.label} ✓</li>)}
          </ol>
          Naostro to funguje stejně, jen každý krok udělá jiný člověk — a vy uvidíte jen ty své,
          v „Moje práce“ pod <em>Na tahu jste vy</em>. Cvičnou zakázku teď smažeme.
        </CModalBody>
        <CModalFooter>
          <CButton color="primary" onClick={() => void finish()}>Smazat cvičnou zakázku a začít naostro</CButton>
        </CModalFooter>
      </CModal>
    );
  }

  return <PracticeBanner step={coach.step} onExit={() => void finish()} />;
}

/**
 * Spustí cvičení: rozdělané naváže, jinak začne krokem 1 na formuláři „Nová zakázka“.
 */
export async function openPractice(navigate: NavigateFunction) {
  const { data } = await api.GET('/api/v1/practice');
  navigate(data ? `/zakazky/${data.contractId}` : '/zakazky/nova?cvicne=1');
}

/**
 * Krok 1 — obchodník zakládá zakázku. Průvodce projde formulář pole po poli; hodnoty jsou
 * předvyplněné příkladem, aby se dalo jen proklikat, ale jdou přepsat.
 */
export function startPracticeForm() {
  const title = `Krok 1 z ${practiceSteps.length} · jste obchodník`;
  const steps = [
    { target: 'nc-customer', text: 'Volá zákazník. Naostro ho vyberete ze seznamu firem v portálu — tady je cvičný.' },
    { target: 'nc-title', text: 'Krátce, o co jde. Podle názvu zakázku najdou i ostatní.' },
    { target: 'nc-items', text: 'Co a kolik se bude stavět. Technické detaily — krabici, antény, parametry — teď neřešíte, doplní je vedení.' },
    { target: 'nc-submit', text: 'Hotovo, zakázku založte.' },
  ]
    .map((s) => ({ element: document.querySelector(`[data-tour="${s.target}"]`), text: s.text }))
    .filter((s): s is { element: Element; text: string } => s.element !== null);

  if (steps.length === 0) return () => {};

  const tour = driver({
    steps: steps.map((s) => ({ element: s.element, popover: { title, description: s.text } })),
    popoverClass: 'st-tour',
    stagePadding: 6,
    stageRadius: 6,
    nextBtnText: 'Další',
    prevBtnText: 'Zpět',
    doneBtnText: 'Rozumím',
    showProgress: false,
  });
  tour.drive();

  return () => tour.destroy();
}
